import {
  type AskAssistant,
  type NavItem,
  type RecordResult,
  type SearchResponse,
  type SearchSection,
  UniversalSearch,
} from '@fountain/universal-search';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { getCampaigns } from '@/api-clients/get-campaigns';
import { getDashboardOpenings } from '@/api-clients/get-dashboard-openings';
import { campaignEditPath } from '@/components/pages/campaigns/campaigns-table/menu';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { usePostHog } from '@/hooks/use-posthog';
import { useSourceFreemium } from '@/hooks/use-source-freemium';
import { useSourceNavItems } from '@/hooks/use-source-nav-items';
import { useWxJwtReady } from '@/hooks/use-wx-jwt-ready';
import { queryKeys } from '@/query-keys';
import { getOpeningDetailsPath } from '@/utils/opening-details-path';

const RESULTS_PER_SECTION = 5;
// Typing back to an earlier query within this window reuses its rows.
const SEARCH_STALE_TIME_MS = 30_000;
// The search param the Dashboard and All Campaigns tables read on mount.
const TABLE_SEARCH_PARAM = 'q';

function withTableSearch(pathname: string, query: string): string {
  return `${pathname}?${new URLSearchParams({ [TABLE_SEARCH_PARAM]: query })}`;
}

// Source has no people records; candidates live in Hire.
async function searchNoPeople() {
  return [];
}

function joinSubtitle(parts: Array<string | null | undefined>) {
  return parts.filter(Boolean).join(' · ') || undefined;
}

type SourceUniversalSearchProps = {
  /** Called after the palette navigates, so the host can remount the page. */
  onNavigated?: () => void;
};

/**
 * Source's adapter for the Cmd+K palette. Searches the records Source lists,
 * through the same endpoints and query params as the Dashboard and All
 * Campaigns search boxes, and lists the pages the sidebar lists.
 */
export function SourceUniversalSearch({
  onNavigated,
}: SourceUniversalSearchProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const isFreemium = useSourceFreemium();
  const isCueReady = useWxJwtReady();
  const sourceNavItems = useSourceNavItems();
  const { dashboardPath, allCampaignsPath } = useNavigationPaths();

  const navItems = useMemo(
    (): NavItem[] =>
      sourceNavItems.map(item => ({
        label: intl.formatMessage(item.label),
        pathname: item.path,
        group: 'Source',
      })),
    [intl, sourceNavItems],
  );

  // The Dashboard table's endpoint, filtered the same way: openings selected
  // for sourcing whose title matches.
  const searchOpenings = useCallback(
    async (query: string): Promise<SearchResponse<RecordResult>> => {
      const data = await queryClient.fetchQuery({
        queryKey: [...queryKeys.dashboard.openings, 'universal-search', query],
        queryFn: () =>
          getDashboardOpenings({
            selectedForSourcing: true,
            query,
            page: 1,
            perPage: RESULTS_PER_SECTION,
          }),
        staleTime: SEARCH_STALE_TIME_MS,
      });
      const results = data.data.map(opening => ({
        id: opening.external_id,
        label: opening.title,
        subtitle: joinSubtitle([opening.location_name, opening.brand_name]),
        pathname: getOpeningDetailsPath({
          dashboardPath,
          openingSlug: opening.slug,
          hasActiveHiringGoal: Boolean(opening.hiring_goal),
          defaultMetricsPeriod: data.default_metrics_period,
        }),
      }));
      return { results, total: data.pagination.total_count };
    },
    [dashboardPath, queryClient],
  );

  // The All Campaigns table's endpoint with its default (lifetime) range, so
  // the count matches the table "See all" opens.
  const searchCampaigns = useCallback(
    async (query: string): Promise<SearchResponse<RecordResult>> => {
      const data = await queryClient.fetchQuery({
        queryKey: [...queryKeys.allCampaigns, 'universal-search', query],
        queryFn: () =>
          getCampaigns({
            search: query,
            page: 1,
            perPage: RESULTS_PER_SECTION,
          }),
        staleTime: SEARCH_STALE_TIME_MS,
      });
      const results = data.campaigns.map(campaign => ({
        id: campaign.id,
        label: campaign.opening_name,
        subtitle: campaign.sourcing_channel.name,
        pathname: campaignEditPath(allCampaignsPath, dashboardPath, campaign),
      }));
      return { results, total: data.total_count };
    },
    [allCampaignsPath, dashboardPath, queryClient],
  );

  const openingsLabel = intl.formatMessage({
    defaultMessage: 'Openings',
    description:
      'Heading over job openings that match a search, as in "See all 12 openings"',
  });
  const campaignsLabel = intl.formatMessage({
    defaultMessage: 'Campaigns',
    description:
      'Heading over sourcing campaigns that match a search, as in "See all 12 campaigns"',
  });

  // A free-tier account has no dashboard, so its openings have no page to
  // open: the routes send both to /openings.
  const searchSections = useMemo((): SearchSection[] => {
    const campaigns: SearchSection = {
      key: 'campaigns',
      label: campaignsLabel,
      search: searchCampaigns,
      viewAllPathname: query => withTableSearch(allCampaignsPath, query),
    };
    if (isFreemium) return [campaigns];
    return [
      {
        key: 'openings',
        label: openingsLabel,
        search: searchOpenings,
        viewAllPathname: query => withTableSearch(dashboardPath, query),
      },
      campaigns,
    ];
  }, [
    allCampaignsPath,
    campaignsLabel,
    dashboardPath,
    isFreemium,
    openingsLabel,
    searchCampaigns,
    searchOpenings,
  ]);

  const askCueLabel = intl.formatMessage({
    defaultMessage: 'Ask Cue',
    description:
      'Action that sends the search text to Cue, the AI assistant, as a question',
  });
  // Cue's bundle loads only once the WX JWT is ready (useWxCopilot).
  const askAssistant = useMemo(
    (): AskAssistant | undefined =>
      isCueReady
        ? {
            label: askCueLabel,
            onAsk: message =>
              window.dispatchEvent(
                new CustomEvent('wx-copilot-command', {
                  detail: {
                    type: 'openCue',
                    payload: { message, openMethod: 'universal_search' },
                  },
                  bubbles: true,
                  composed: true,
                }),
              ),
          }
        : undefined,
    [askCueLabel, isCueReady],
  );

  const onNavigate = useCallback(
    (pathname: string) => {
      navigate(pathname);
      onNavigated?.();
    },
    [navigate, onNavigated],
  );

  const onAnalytics = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      switch (event) {
        case 'universal_search:bar_open':
          posthog.capture('universal_search:bar_open', properties);
          break;
        case 'universal_search:result_click':
          posthog.capture('universal_search:result_click', properties);
          break;
        default:
          break;
      }
    },
    [posthog],
  );

  return (
    <UniversalSearch
      searchPeople={searchNoPeople}
      searchSections={searchSections}
      navItems={navItems}
      onNavigate={onNavigate}
      onAnalytics={onAnalytics}
      askAssistant={askAssistant}
      placeholder={intl.formatMessage({
        defaultMessage: 'Search openings, campaigns and pages…',
        description:
          'Placeholder in the search box that finds job openings, sourcing campaigns and app pages',
      })}
      emptyStateHint={intl.formatMessage({
        defaultMessage: 'Try an opening title or a page name.',
        description:
          'Hint shown when a search finds nothing, suggesting what to search for instead',
      })}
    />
  );
}
